// This function can be used to test that the generatePassword() never emits repeated characters.
// Should probably move this into the actual tests at some point but I wanted to at least keep this around in case it needs more changes.
function testGenerateLinePassword(iterations = 10000): void {
  console.log(`Testing generateLinePassword() ${iterations} times...`);

  for (let i = 0; i < iterations; i++) {
    const password = generateLinePassword();

    if (hasConsecutiveRepeatedChars(password)) {
      throw new Error(`Test failed on iteration ${i + 1}: Password "${password}" contains consecutive repeated characters`);
    }

    // Optional: log progress every 1000 iterations
    if ((i + 1) % 1000 === 0) {
      console.log(`✓ ${i + 1} passwords generated successfully`);
    }
  }

  console.log(`✅ All ${iterations} passwords passed the test!`);
}

export function randomNumber(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min; // The max & min are inclusive
}

export function randomCharacterFromString(searchString: string): string {
  return searchString[randomNumber(0, searchString.length - 1)];
}

export function randomUppercaseLetter(): string {
  return randomCharacterFromString('ABCDEFGHIJKLMNOPQRSTUVWXYZ');
}

export function randomSpecialCharacter(): string {
  return randomCharacterFromString('~!#$%^&*_-+={[}]|;<,>.?'); // remove : and @
}

export function randomAnyCharacter(): string {
  return randomCharacterFromString('~!#$%^&*_-+={[}]|;<,>.?ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'); // remove : and @
}

export function randomCharacters(minLength: number, maxLength: number): string {
  const strLen = randomNumber(minLength, maxLength);
  let randomString = '';
  for (let i = 0; i < strLen; i++) {
    randomString = `${randomString}${randomAnyCharacter()}`;
  }
  return randomString;
}

export function hasConsecutiveRepeatedChars(str: string): boolean {
  let found = false;
  for (let i = 0; i < str.length - 1; i++) {
    if (str[i] === str[i + 1]) {
      found = true;
    }
  }
  return found;
}

export function shufflePassword(origPassword: string): string {
  let newPassword = '';
  let password = origPassword;
  let i = -1;
  let len = -1;
  while (password.length > 0) {
    len = password.length;
    i = randomNumber(0, len - 1);
    newPassword = `${newPassword}${password[i]}`;
    password = `${password.substring(0, i)}${password.substring(i + 1)}`;
  }
  return newPassword;
}

export function generateLinePassword(): string {
  // include at least 1 uppercase letter
  // include at least 1 number
  // include at least 1 special symbol
  // add 5-12 more random characters (length = 8-15 characters)
  // shuffle password
  let password: string;
  do {
    password = shufflePassword(`${randomUppercaseLetter()}${randomNumber(0, 9)}${randomSpecialCharacter()}${randomCharacters(5, 12)}`);
  } while (hasConsecutiveRepeatedChars(password))
  return password;
}

export function generateLinePassword2(): string {
  // include at least 1 uppercase letter
  // include at least 1 number
  // include at least 1 special symbol
  // add 5-12 more random characters (length = 8-15 characters)
  // shuffle password
  return shufflePassword(`${randomUppercaseLetter()}${randomNumber(0, 9)}${randomSpecialCharacter()}${randomCharacters(5, 12)}`);
}

export function generateLinePassword3(): string {
  // include at least 1 uppercase letter
  // include at least 1 number
  // include at least 1 special symbol
  // add 9 more random characters (length = 12 characters)
  // shuffle password
  return shufflePassword(`${randomUppercaseLetter()}${randomNumber(0, 9)}${randomSpecialCharacter()}${randomCharacters(9, 9)}`);
}

export function validPIN(pin: string): boolean {

  let index = 0;
  while (index + 2 < pin.length) {
    // no sequences of 3 digits
    if (+pin[index + 2] === +pin[index + 1] + 1 && +pin[index + 1] === +pin[index] + 1) {
      return false;
    }

    // no repeat of 3 digits
    if (+pin[index + 2] === +pin[index + 1] && +pin[index + 1] === +pin[index]) {
      return false;
    }
    index++;
  }

  return true;
}

export function isAllSequentialDigits(s: string): boolean {
  const longSequence = '01234567890123456789012345678901234567890123456789012345678901234567890123456789012345678901234567890';
  return longSequence.indexOf(s) !== -1;
}

export function generateLinePIN(pinLength = 4): string {
  // generates a pin of random numbers (default length is 4)
  let pin = '';
  while (pin.length < pinLength) {
    pin += randomNumber(0, 9);
  }
  // if pin not valid, try again
  return validPIN(pin) ? pin : generateLinePIN(pinLength);
}

export function generateVoicemailPIN(pinLength = 6): string {

  // generates a pin of pinLength random numbers (default length is 6)
  return generateLinePIN(pinLength);
}

export function validMetaswitchPassword(password: string): boolean {
  return !(
    (!password)
    || (password.length < 8 || password.length > 15)
    || ( !/.*[A-Z].*/.test(password) )
    || ( !/.*[0-9].*/.test(password) )
    || ( !/.*[~!@#$%^&*_\-+={[}\]|:;<,>.?].*/.test(password) )
    || ( /.*[()].*/.test(password) )
    || ( /(.)\1{2,}/.test(password) )
  );
}
